// utils/formatUtils.js

/**
 * Masks a phone number by hiding middle digits
 * @param {string} phoneNumber - The phone number to mask
 * @param {Object} options - Masking options
 * @param {number} options.visibleStart - Number of digits to show at start (default: 2)
 * @param {number} options.visibleEnd - Number of digits to show at end (default: 2)
 * @param {string} options.maskChar - Character to use for masking (default: '*')
 * @returns {string} - Masked phone number
 *
 * @example
 * maskPhoneNumber('9876543210') // Returns: '98******10'
 * maskPhoneNumber('+919876543210') // Returns: '+91 98******10'
 * maskPhoneNumber('9876543210', { visibleStart: 3, visibleEnd: 3 }) // Returns: '987****210'
 */
export const maskPhoneNumber = (phoneNumber, options = {}) => {
    if (!phoneNumber) return '';

    const {
        visibleStart = 2,
        visibleEnd = 2,
        maskChar = '*'
    } = options;

    // Remove all non-digit characters except + at the start
    const hasPlus = phoneNumber.startsWith('+');
    const digits = phoneNumber.replace(/\D/g, '');

    if (digits.length === 0) return phoneNumber;

    // Extract country code if present (assuming +91 for India or similar)
    let countryCode = '';
    let phoneDigits = digits;

    if (hasPlus && digits.length > 10) {
        // Extract country code (typically 1-3 digits)
        if (digits.length === 12) { // +91 XXXXXXXXXX
            countryCode = `+${digits.slice(0, 2)} `;
            phoneDigits = digits.slice(2);
        } else if (digits.length === 11) { // +1 XXXXXXXXXX
            countryCode = `+${digits.slice(0, 1)} `;
            phoneDigits = digits.slice(1);
        } else if (digits.length === 13) { // +XXX XXXXXXXXXX
            countryCode = `+${digits.slice(0, 3)} `;
            phoneDigits = digits.slice(3);
        }
    }

    // Calculate masking
    const totalDigits = phoneDigits.length;
    const maskLength = Math.max(0, totalDigits - visibleStart - visibleEnd);

    if (totalDigits <= visibleStart + visibleEnd) {
        // If phone number is too short, mask middle portion
        const start = phoneDigits.slice(0, Math.floor(totalDigits / 2));
        const end = phoneDigits.slice(Math.floor(totalDigits / 2));
        return countryCode + start + maskChar.repeat(2) + end;
    }

    // Mask the middle portion
    const startDigits = phoneDigits.slice(0, visibleStart);
    const endDigits = phoneDigits.slice(-visibleEnd);
    const maskedPortion = maskChar.repeat(maskLength);

    return countryCode + startDigits + maskedPortion + endDigits;
};

/**
 * Masks an email address
 * @param {string} email - The email to mask
 * @param {Object} options - Masking options
 * @param {number} options.visibleChars - Number of characters to show before @ (default: 2)
 * @param {string} options.maskChar - Character to use for masking (default: '*')
 * @returns {string} - Masked email
 *
 * @example
 * maskEmail('example@gmail.com') // Returns: 'ex*****@gmail.com'
 */
export const maskEmail = (email, options = {}) => {
    if (!email || !email.includes('@')) return email;

    const {
        visibleChars = 2,
        maskChar = '*'
    } = options;

    const [localPart, domain] = email.split('@');

    if (localPart.length <= visibleChars) {
        return email;
    }

    const visiblePart = localPart.slice(0, visibleChars);
    const maskedLength = Math.max(3, localPart.length - visibleChars);
    const masked = maskChar.repeat(maskedLength);

    return `${visiblePart}${masked}@${domain}`;
};

/**
 * Formats a phone number for display (adds spacing)
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number
 *
 * @example
 * formatPhoneNumber('9876543210') // Returns: '98765 43210'
 * formatPhoneNumber('+919876543210') // Returns: '+91 98765 43210'
 */
export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    const hasPlus = phoneNumber.startsWith('+');
    const digits = phoneNumber.replace(/\D/g, '');

    if (digits.length === 0) return phoneNumber;

    // Format based on length
    if (hasPlus && digits.length === 12) { // +91 XXXXXXXXXX
        return `+${digits.slice(0, 2)} ${digits.slice(2, 7)} ${digits.slice(7)}`;
    } else if (digits.length === 10) { // XXXXXXXXXX
        return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    } else if (hasPlus && digits.length === 11) { // +1 XXXXXXXXXX
        return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }

    return phoneNumber;
};

/**
 * Validates if a string is a valid phone number
 * @param {string} phoneNumber - The phone number to validate
 * @param {Object} options - Validation options
 * @param {number} options.minLength - Minimum length (default: 10)
 * @param {number} options.maxLength - Maximum length (default: 13)
 * @returns {boolean} - True if valid phone number
 */
export const isValidPhoneNumber = (phoneNumber, options = {}) => {
    if (!phoneNumber) return false;

    const { minLength = 10, maxLength = 13 } = options;
    const digits = phoneNumber.replace(/\D/g, '');

    return digits.length >= minLength && digits.length <= maxLength;
};
